import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const klar: Competitor = {
  "id": "klar",
  "name": "Klar",
  "group": "ecomm",
  "website": "https://getklar.com/",
  "owner": "Independent, privately held (Klar Insights GmbH, Munich, Germany; no acquisition found)",
  "summary": "Klar is a Munich-based ecommerce analytics platform that combines profit reporting (contribution margins), retention and cohort analysis, and marketing measurement (first-party tracking, multi-touch attribution, MMM and geo-lift tests). It is aimed mainly at European DTC brands on Shopify, Shopware and similar storefronts.",
  "bestFor": [
    "European DTC brands that want contribution-margin P&L, retention cohorts and attribution in one tool with EU data hosting and ISO 27001",
    "Shopify or Shopware brands from low-seven figures upward that want a usable setup within days and pricing starting around €200/month",
    "Teams that want MTA, MMM and incrementality tests calibrated together without hiring a data scientist"
  ],
  "notFor": [
    "Brands that need their modeled data in their own warehouse to query with SQL or join to other business data",
    "Teams that need fully custom dashboards or custom formula metrics",
    "Wholesale- or ERP-heavy businesses needing native NetSuite, Faire or retail POS connectors"
  ],
  "strengths": [
    "Prebuilt contribution margin waterfall (CM1/CM2 with COGS, logistics and payment fees) and retention views (layer cake, CLV development vs CAC, retention by channel/product/AOV) on day one, which a custom dbt + Sigma build would need to model and validate",
    "Own first-party server-side pixel with multiple attribution models plus MMM and geo-lift incrementality in a unified measurement engine, which is hard to build in-house",
    "Klar advertises onboarding in about 2 hours and customers cite days to a week, versus weeks to months for a custom warehouse",
    "Pricing from €200/month (or €400/month with attribution) with free onboarding, no setup fee and no long-term contract, well below consultant plus warehouse plus BI license costs",
    "EU data hosting, GDPR focus and ISO 27001 certification out of the box"
  ],
  "limitations": [
    "Klar's own 2026 comparison article says it does not run a database you can query directly; data leaves only through its Public API (attribution, marketing and revenue/profit reports)",
    "The same Klar article says there is no custom dashboard builder yet and calls this the most frequent criticism in its reviews",
    "Flex Table lets users pick from predefined metrics and export XLSX; custom formulas are not documented",
    "TikTok Shop orders arrive only through the Shopify sync; ERPs and custom shops must push orders through Klar's inbound API rather than native connectors",
    "Amazon data syncs daily with 30 days of history, and Amazon and WooCommerce COGS must be added manually",
    "Klar's own article says its in-product AI lags category leaders, and its MCP option is a do-it-yourself local server built on the Public API"
  ],
  "pricing": {
    "model": "Revenue-based (trailing 12-month net revenue)",
    "publicStartingPrice": "Core from €200/month; Core + Attribution from €400/month",
    "freeTier": "No free tier; 14-day free trial that starts after the account is fully set up",
    "notes": "Two plans: Core (profitability, retention and acquisition reporting; unlimited users, integrations and support) and Core + Attribution (adds first-party tracking pixel and the attribution/measurement suite). Price is based on net revenue over the last 12 months after returns and taxes; monthly plans are recalculated every 3 months, annual plans every 12. Free onboarding, no setup cost, no long-term contract. Prices are listed in EUR; Shopify App Store listing says charges are billed in USD outside Shopify. Setup: Klar advertises onboarding within about 2 hours; customer quotes on its site cite a few days to a week.",
    "sourceUrl": "https://getklar.com/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "One-click Shopify integration with hourly order sync and COGS, taxes, returns",
      "sourceUrl": "https://help.getklar.com/en/articles/9805630-available-information-by-shop-system-and-cost-order-data-pull-frequency"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon FBA/FBM orders and Amazon Ads; daily sync, 30 days history, COGS entered manually",
      "sourceUrl": "https://help.getklar.com/en/articles/9805630-available-information-by-shop-system-and-cost-order-data-pull-frequency"
    },
    "tiktok_shop": {
      "value": "partial",
      "note": "TikTok Shop orders come through the Shopify sync; TikTok ad spend is a separate source",
      "sourceUrl": "https://help.getklar.com/en/articles/15444400-tiktok-shop-data-in-klar"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "No native ERP/wholesale/POS connectors listed; orders and costs can be pushed via inbound API",
      "sourceUrl": "https://help.getklar.com/en/articles/9345461-klar-api-introduction"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta, Google, TikTok, Pinterest, Bing, Snapchat, Amazon Ads, Criteo, Taboola, Outbrain",
      "sourceUrl": "https://getklar.com/set-up"
    },
    "first_party_pixel_attribution": {
      "value": "yes",
      "note": "First-party pixel and multi-touch attribution on Core + Attribution plan",
      "sourceUrl": "https://getklar.com/pricing"
    },
    "mmm_incrementality": {
      "value": "yes",
      "note": "MMM model plus geo-lift incrementality tests used to calibrate it",
      "sourceUrl": "https://getklar.com/features/unified-marketing-measurement"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "CM1 (net revenue minus COGS) and CM2 (minus logistics and payment fees)",
      "sourceUrl": "https://help.getklar.com/en/articles/6125307-configuring-transaction-costs"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "Revenue layer cake, CLV development vs CAC, retention by channel/product/first AOV",
      "sourceUrl": "https://help.getklar.com/en/articles/6127394-retention-trends"
    },
    "subscription_analytics": {
      "value": "unknown",
      "note": "No Recharge or subscription analytics found in Klar docs",
      "sourceUrl": "https://help.getklar.com/en/collections/16763773-faq"
    },
    "warehouse_access": {
      "value": "no",
      "note": "Klar says it does not run a database you can query; Public API exposes report-level data",
      "sourceUrl": "https://getklar.com/post/ecommerce-attribution-tools"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "Custom dimensions, channel groupings and custom costs; no custom formula metrics documented",
      "sourceUrl": "https://help.getklar.com/en/articles/6127702-marketing-channel-overview"
    },
    "custom_sql": {
      "value": "no",
      "note": "No queryable database or SQL editor, per Klar's own comparison",
      "sourceUrl": "https://getklar.com/post/ecommerce-attribution-tools"
    },
    "ai_assistant": {
      "value": "unknown",
      "note": "Klar says its in-product AI trails leaders; MCP is a DIY local server via Public API",
      "sourceUrl": "https://help.getklar.com/en/articles/15617889-build-a-klar-attribution-mcp-server-with-claude"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app found",
      "sourceUrl": "https://getklar.com/llms.txt"
    }
  },
  "recentChanges": [
    {
      "date": "2026",
      "change": "Unified measurement engine combining MTA, MMM, geo-lift incrementality and post-purchase survey data; data-driven attribution model documented",
      "sourceUrl": "https://getklar.com/features/unified-marketing-measurement"
    },
    {
      "date": "2026",
      "change": "TikTok Shop data supported out of the box via Shopify sync",
      "sourceUrl": "https://help.getklar.com/en/articles/15444400-tiktok-shop-data-in-klar"
    },
    {
      "date": "2026",
      "change": "Public API with attribution, marketing and revenue/profit reports, plus a guide to building a Klar MCP server with Claude",
      "sourceUrl": "https://help.getklar.com/en/articles/15617889-build-a-klar-attribution-mcp-server-with-claude"
    }
  ],
  "sources": [
    {
      "title": "Klar Pricing",
      "url": "https://getklar.com/pricing"
    },
    {
      "title": "Klar Set Up",
      "url": "https://getklar.com/set-up"
    },
    {
      "title": "Klar About Us",
      "url": "https://www.getklar.com/about"
    },
    {
      "title": "Klar llms.txt",
      "url": "https://getklar.com/llms.txt"
    },
    {
      "title": "Klar Analytics on Shopify App Store",
      "url": "https://apps.shopify.com/klar-analytics"
    },
    {
      "title": "Available information by shop system and data pull frequency",
      "url": "https://help.getklar.com/en/articles/9805630-available-information-by-shop-system-and-cost-order-data-pull-frequency"
    },
    {
      "title": "TikTok Shop Data in Klar",
      "url": "https://help.getklar.com/en/articles/15444400-tiktok-shop-data-in-klar"
    },
    {
      "title": "Klar API Introduction",
      "url": "https://help.getklar.com/en/articles/9345461-klar-api-introduction"
    },
    {
      "title": "Build a Klar Attribution MCP Server with Claude",
      "url": "https://help.getklar.com/en/articles/15617889-build-a-klar-attribution-mcp-server-with-claude"
    },
    {
      "title": "Klar Unified Measurement Engine",
      "url": "https://getklar.com/features/unified-marketing-measurement"
    },
    {
      "title": "Marketing Mix Model (MMM) Attribution",
      "url": "https://help.getklar.com/en/articles/8637467-marketing-mix-model-mmm-attribution"
    },
    {
      "title": "Retention Trends",
      "url": "https://help.getklar.com/en/articles/6127394-retention-trends"
    },
    {
      "title": "Configuring Transaction Costs",
      "url": "https://help.getklar.com/en/articles/6125307-configuring-transaction-costs"
    },
    {
      "title": "Contribution Margin 1 (CM1)",
      "url": "https://help.getklar.com/en/articles/6127773-contribution-margin-1-cm1"
    },
    {
      "title": "Marketing: Flex Table",
      "url": "https://help.getklar.com/en/articles/6127702-marketing-channel-overview"
    },
    {
      "title": "Klar FAQ",
      "url": "https://help.getklar.com/en/collections/16763773-faq"
    },
    {
      "title": "Best Ecommerce Attribution Tools in 2026 (Klar blog)",
      "url": "https://getklar.com/post/ecommerce-attribution-tools"
    }
  ],
  "lastReviewed": "2026-11-03"
};
